import React, { useEffect } from "react"
import TagManager from "react-gtm-module"

import { graphql } from "gatsby"
import { ArticleQuery, DatoCmsAllLocalesForDatoCmsArticleSlug, DatoCmsSlugsConfiguration } from "../../../graphql-types"

import * as config from "../../../config.js"
import * as styles from "./article-page.module.scss"
import Layout from "../../components/layout/layout"
import SEO from "../../components/seo"
import HrefLangLinks from "../../components/layout/href-lang-links/href-lang-links"
import Cover from "./cover/cover"
import RichDatoCmsContent from "../../components/common/jsx/rich-dato-cms-content/rich-dato-cms-content"
import ReadAlso from "./read-also/read-also"
import useSlugValue from "../../hooks/use-slug-value"
import * as stylesNewsletter from "../../components/newsletter/newsletter.module.scss"

type ArticlePageProps = {
  data: ArticleQuery
  pageContext: {
    locale: string
    defaultLocale: string
    slugs: DatoCmsSlugsConfiguration
    id: string
    articlePageSlug: string
    allSlugLocales: [DatoCmsAllLocalesForDatoCmsArticleSlug]
  }
}

const ArticlePage: React.FC<ArticlePageProps> = ({ data, pageContext }) => {
  useEffect(() => {
    if (config.google_tag_manager_id) {
      TagManager.initialize({ gtmId: config.google_tag_manager_id })
      TagManager.dataLayer({
        dataLayer: {
          environment: process.env.NODE_ENV,
          language: pageContext.locale,
          pageTemplate: "articles",
          categorieArticle: article.category.label,
          articleTag: article.tags.map(art => Object.values(art)).join(", "),
        },
      })
    }
  }, [])

  const article = data.datoCmsArticle
  const breadcrumb = [
    {
      link: data.datoCmsSlugsConfiguration.projectsNewsInsightsPageSlug,
      title: data.datoCmsPniPage.headerTitle,
    },
    {
      link: `${data.datoCmsSlugsConfiguration.projectsNewsInsightsPageSlug}/${article.slug}`,
      title: article.articleTitle,
    },
  ]
  const pniSlug = useSlugValue(pageContext.slugs._allProjectsNewsInsightsPageSlugLocales, pageContext.locale)

  return (
    <Layout
      pageContext={pageContext}
      allSlugLocales={pageContext.allSlugLocales}
      currentRoute={pniSlug}
      headerConfig={data.datoCmsHeader}
      footerConfig={data.datoCmsFooter}
    >
      <Cover
        data={article}
        fields={data.datoCmsFieldsArticlePage}
        searchRequestSlug={
          pageContext.locale === pageContext.defaultLocale ? pniSlug : `${pageContext.locale}/${pniSlug}`
        }
      />
      <section className={styles.container}>
        <RichDatoCmsContent data={article.richContent} />
      </section>

      {article.readAlsoArticles.length > 0 && (
        <ReadAlso
          title={article.readAlsoTitle}
          readAlsoArticles={article.readAlsoArticles}
          locale={pageContext.locale}
          projectsNewsInsightsPageSlug={data.datoCmsSlugsConfiguration.projectsNewsInsightsPageSlug}
        />
      )}
      <section className={stylesNewsletter.container}  style={{ marginTop: "64px" }}>
        <div className={stylesNewsletter.iframe} id="newsletter">
          {pageContext.locale === pageContext.defaultLocale ? (
            <iframe
              title="Landing Page"
              src="https://f.inno.keolis.com/f/l/?q=BSLqpzmY16RtCSEShgohLdWayZ7VLugElOIBd2us19BPYmcYHtWnKpZ_ghOHlwepKS8vEt2XF5fgJVpUTxggfGgh-u3ecnw4-iMJYaJL4PUWZ0fQ30PVivkfxCsaGeXFURFY1n9rGUovvmn4gjhnLYdWzv1jAMrtWxpTPOZmjilw2he_luP-qSjZukWgwL7LDF2AE08YfAU1ZfHF9nGi1cx52MZS_6zv-5JbE27ctJ5w4kkP_keKx-Cw5tdC2TvE"
            />
          ) : (
            <iframe
              title="Landing Page"
              src="https://f.inno.keolis.com/f/l/?q=BSLqpzmY16RtCSEShgohLTb9NHftVXSchI5U5X7tFck1wSvnK6Zju4LY9embQyGwFPQdea4JaeTvJx99yXHXclaUhOvyWW_WeYDVvlSeXvuqssfSD2RlonLzJpgW8R20QGbQNQqp7rusHzAE9gDZIjcD53XmVjJPoxHfCcN2-4__pURFfpdMI14KVPoBL9VUVewRCg07AwrLLZ33NS3YaE_sOSd8bVgxSs-DXSo3dHT9AsYS-1K3UrI9nal2LqNc"
            />
          )}
        </div>
      </section>
    </Layout>
  )
}

export const Head: React.FC<ArticlePageProps> = ({ data, pageContext }) => {
  const defaultDomain = "https://innovation.keolis.com"
  return (
    <>
      <SEO
        title={data.datoCmsArticle.title}
        description={data.datoCmsArticle.metaDescription}
        locale={pageContext.locale}
      />
      {typeof window !== "undefined" && (
        <link
          rel="canonical"
          href={data.datoCmsArticle.isCanonical ? data.datoCmsArticle.urlCanonical : window.location.href}
        />
      )}
      {typeof window !== "undefined" && (
        <>
          {/*<link
            rel="canonical"
            href={data.datoCmsPage.isCanonical ? data.datoCmsPage.urlCanonical : window.location.href}
          />*/}
          <HrefLangLinks
            defaultDomain={window.location.origin}
            defaultLocale={pageContext.defaultLocale}
            slugs={[pageContext.slugs._allProjectsNewsInsightsPageSlugLocales, pageContext.allSlugLocales]}
          />
        </>
      )}
    </>
  )
}

export const pageQuery = graphql`
  query Article($locale: String!, $id: String!) {
    datoCmsGlobalConfiguration {
      locales
    }
    datoCmsSlugsConfiguration(locale: $locale) {
      projectsNewsInsightsPageSlug
    }
    datoCmsPniPage(locale: $locale) {
      ...HeaderPNIFields
    }
    datoCmsHeader(locale: $locale) {
      ...HeaderFields
    }
    datoCmsFooter(locale: $locale) {
      ...FooterFields
    }
    datoCmsArticle(locale: $locale, id: { eq: $id }) {
      ...ArticleFields
    }
    datoCmsArticleReceiveNewsletter(locale: $locale) {
      ...ArticleReceiveNewsletterFields
    }
    datoCmsFieldsArticlePage(locale: $locale) {
      ...CoverArticleFields
    }
  }
`

export const fragment = graphql`
  fragment ArticleFields on DatoCmsArticle {
    __typename
    id
    title
    metaDescription
    slug
    isCanonical
    urlCanonical
    articleTitle
    description
    author
    preposition
    authorPosition
    date
    thumbnailImage {
      alt
      format
      url
    }
    thumbnailTitle
    thumbnailBackgroundColor {
      hex
    }
    thumbnailTextColor {
      hex
    }
    thumbnailIconColor {
      hex
    }
    thumbnailCircleColor {
      hex
    }
    category {
      label
    }
    tags {
      label
    }
    partners {
      link
      logo {
        alt
        format
        title
        url
      }
    }
    image {
      alt
      format
      title
      url
    }
    richContent {
      ...EmbedSocialMediaV2Fields
      ...ArticleStructuredTextFields
      ...ArticleImageFields
      ...ArticleQuoteFields
      ...ArticleLargeKeyFigureFields
      ...ArticleKeyFigureRowFields
      ...ArticleKeyFigureRowV2Fields
      ...ArticleImageTextOverlayFields
      ...ArticleEmbedFields
      ...ArticleSliderFields
      ...ArticleVideoFields
      ...ArticlePdfFields
      ...FramedTextFields
    }
    readAlsoTitle
    readAlsoArticles {
      ...ReadAlsoFields
    }
  }
`

export default ArticlePage
